import React, { useCallback } from 'react';
import { useGetDashboardOverviewQuery } from '../../features/templates/templatesApi';
import { useAuthSlice } from '../../features/auth/authSlice';
import { useGuildsSlice } from '../../features/guilds/guildsSlice';
import { useUISlice } from '../../features/ui/uiSlice';
import { ErrorMessage } from '../common/ErrorMessage';
import { SkeletonDashboard } from '../common/Skeleton';
import { openExternalLink } from '../../utils/discord';
import type { DashboardGuildSummary, DashboardRecentMessage } from '../../types';

function formatRelative(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function guildIconUrl(guildId: string, icon: string | null): string | null {
  if (!icon) return null;
  return `https://cdn.discordapp.com/icons/${guildId}/${icon}.png`;
}

export const Dashboard: React.FC = () => {
  const authSlice = useAuthSlice();
  const guildsSlice = useGuildsSlice();
  const uiSlice = useUISlice();

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useGetDashboardOverviewQuery(undefined, {
    skip: !authSlice.auth?.access_token,
  });

  const handleOpenGuild = useCallback(
    (guildId: string) => {
      guildsSlice.setSelectedGuild(guildId);
      uiSlice.setView('templates');
    },
    [guildsSlice, uiSlice]
  );

  const handleOpenMessage = useCallback((m: DashboardRecentMessage) => {
    void openExternalLink(
      `https://discord.com/channels/${m.guild_id}/${m.channel_id}/${m.message_id}`
    );
  }, []);

  if (!authSlice.auth?.access_token || isLoading || isFetching) {
    return <SkeletonDashboard />;
  }

  if (error || !data) {
    return <ErrorMessage message="Failed to load dashboard. Please try again." />;
  }

  const { guilds, recent_messages, totals } = data;

  // No watched guilds yet — onboarding prompt
  if (guilds.length === 0) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-surface-container flex items-center justify-center mb-6">
            <span className="material-symbols-outlined text-4xl text-on-surface-variant">
              dns
            </span>
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight text-on-surface mb-2">
            Track your first server
          </h2>
          <p className="text-on-surface-variant text-sm max-w-md mb-8">
            Add a server you administer to start managing role templates and see
            activity here.
          </p>
          <button
            onClick={() => uiSlice.setView('guilds')}
            className="gradient-primary text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-xl">add</span>
            Add a Server
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 px-1">
        <h1 className="text-3xl font-extrabold tracking-tight text-on-surface mb-2">
          Dashboard
        </h1>
        <p className="text-on-surface-variant text-sm">
          Overview of your tracked servers.
        </p>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { label: 'Templates', value: totals.templates, icon: 'layers' },
          { label: 'Live Messages', value: totals.messages, icon: 'chat_bubble' },
          { label: 'Servers', value: totals.servers, icon: 'dns' },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-surface-container-low rounded-lg p-4 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-xl text-primary-text">
                {s.icon}
              </span>
            </div>
            <div>
              <div className="text-2xl font-bold text-on-surface leading-none">{s.value}</div>
              <div className="text-[11px] text-on-surface-variant uppercase tracking-wider mt-1">
                {s.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-3 px-1">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-on-surface-variant">
            Recent Activity
          </h2>
        </div>
        {recent_messages.length === 0 ? (
          <div className="bg-surface-container-low rounded-lg px-4 py-6 text-center">
            <p className="text-sm text-on-surface-variant">
              Nothing deployed yet. Use{' '}
              <code className="bg-surface-container px-1.5 py-0.5 rounded text-xs">
                /send-template
              </code>{' '}
              in Discord to deploy a role selection message.
            </p>
          </div>
        ) : (
          <ul className="bg-surface-container-low rounded-lg divide-y divide-outline-variant/10">
            {recent_messages.map((m) => {
              const ts = m.last_edited_at ?? m.sent_at;
              const wasEdited = m.last_edited_at != null;
              return (
                <li key={m.id}>
                  <button
                    onClick={() => handleOpenMessage(m)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-container transition-colors text-left"
                  >
                    <span className="w-2 h-2 rounded-full bg-success flex-shrink-0" />
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <span className="text-sm font-medium text-on-surface truncate">
                        {m.template_name}
                      </span>
                      <span className="text-on-surface-variant text-xs">in</span>
                      <span className="text-xs text-on-surface-variant flex items-center gap-1 truncate">
                        <span className="material-symbols-outlined text-[14px]">tag</span>
                        {m.channel_name}
                      </span>
                      <span className="text-on-surface-variant/70 text-xs">·</span>
                      <span className="text-xs text-on-surface-variant truncate">
                        {m.guild_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[11px] text-on-surface-variant">
                        {wasEdited ? 'Edited ' : ''}
                        {formatRelative(ts)}
                      </span>
                      <span className="material-symbols-outlined text-base text-on-surface-variant">
                        open_in_new
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Per-server grid */}
      <section>
        <div className="flex items-center justify-between mb-3 px-1">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-on-surface-variant">
            Your Servers
          </h2>
          <button
            onClick={() => uiSlice.setView('guilds')}
            className="text-xs text-primary-text hover:underline font-medium"
          >
            Manage
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {guilds.map((g: DashboardGuildSummary) => (
            <ServerSummaryCard
              key={g.guild_id}
              guild={g}
              onOpen={handleOpenGuild}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

interface ServerSummaryCardProps {
  guild: DashboardGuildSummary;
  onOpen: (guildId: string) => void;
}

const ServerSummaryCard: React.FC<ServerSummaryCardProps> = ({ guild, onOpen }) => {
  const iconUrl = guildIconUrl(guild.guild_id, guild.icon);
  return (
    <button
      onClick={() => onOpen(guild.guild_id)}
      className="group bg-surface-container-low hover:bg-surface-container transition-colors rounded-lg p-5 text-left flex flex-col gap-4"
    >
      <div className="flex items-center gap-3">
        {iconUrl ? (
          <img
            src={iconUrl}
            alt={guild.name}
            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-secondary-container text-on-secondary-container flex items-center justify-center font-semibold flex-shrink-0">
            {guild.name.charAt(0).toUpperCase()}
          </div>
        )}
        <h3 className="text-on-surface font-semibold truncate flex-1">{guild.name}</h3>
        <span className="material-symbols-outlined text-on-surface-variant group-hover:text-primary-text transition-colors text-xl">
          chevron_right
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xl font-bold text-on-surface leading-none">
            {guild.template_count}
          </div>
          <div className="text-[11px] text-on-surface-variant uppercase tracking-wider mt-1">
            {guild.template_count === 1 ? 'Template' : 'Templates'}
          </div>
        </div>
        <div>
          <div className="text-xl font-bold text-on-surface leading-none">
            {guild.message_count}
          </div>
          <div className="text-[11px] text-on-surface-variant uppercase tracking-wider mt-1">
            Live {guild.message_count === 1 ? 'Message' : 'Messages'}
          </div>
        </div>
      </div>

      {guild.last_activity != null && (
        <div className="text-[11px] text-on-surface-variant flex items-center gap-1">
          <span className="material-symbols-outlined text-[14px]">schedule</span>
          Last activity {formatRelative(guild.last_activity)}
        </div>
      )}
    </button>
  );
};
